import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { CheckoutButton } from "@/lib/checkout";
import CurrencySwitcher from "@/components/CurrencySwitcher";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Video, Check } from "lucide-react";
import { useTr } from "@/i18n/translator";

export default function Pricing() {
  const tr = useTr();
  const [plans, setPlans] = useState([]);
  const [period, setPeriod] = useState("monthly"); // monthly | annual
  const [addVideo, setAddVideo] = useState(false);
  const { user } = useAuth();
  const { format, info } = useCurrency();

  useEffect(() => {
    api.get("/pricing").then(({ data }) => setPlans(data.plans));
  }, []);

  const planKey = (pid) => {
    if (pid === "free") return "free";
    const v = addVideo ? "_video" : "";
    const a = period === "annual" ? "_annual" : "";
    return `${pid}${v}${a}`;
  };

  const priceFor = (p) => {
    let base, videoAdd, per, savings;
    if (period === "annual") {
      base = p.annual_price_usd;
      videoAdd = p.video_annual_price_usd - p.annual_price_usd;
      per = "año";
      savings = addVideo ? p.annual_savings * 2 : p.annual_savings;
    } else {
      base = p.price_usd;
      videoAdd = p.video_price_usd - p.price_usd;
      per = "mes";
      savings = 0;
    }
    return { total: base + (addVideo ? videoAdd : 0), base, videoAdd, per, savings };
  };

  return (
    <div className="min-h-screen bg-[#F4F4F0] text-[#0A0A0A]">
      <header className="sticky top-0 z-40 bg-[#F4F4F0] border-b border-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="pricing-logo">
            <img src="/logo-cia.png" alt="CIA Marketing" className="w-10 h-10 object-contain" />
            <span className="font-display text-lg">CIA Marketing<span className="text-[#E53935]">®</span></span>
          </Link>
          {user ? (
            <div className="flex items-center gap-2">
              <LanguageSwitcher />
              <CurrencySwitcher />
              <Link to="/dashboard" className="font-mono-mark text-[11px] bg-[#0A0A0A] text-white px-4 py-2 border border-[#0A0A0A]" data-testid="pricing-header-dashboard">
                {tr("VOLVER AL DASHBOARD")}
              </Link>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <LanguageSwitcher />
              <CurrencySwitcher />
              <Link to="/register" className="font-mono-mark text-[11px] bg-[#0A0A0A] text-white px-4 py-2 border border-[#0A0A0A]" data-testid="pricing-header-cta">
                {tr("EMPEZAR GRATIS")}
              </Link>
            </div>
          )}
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24" data-testid="pricing-page">
        <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("TARIFA · TRANSPARENTE")}</div>
        <h1 className="font-display text-5xl md:text-6xl mt-3 max-w-3xl leading-none">{tr("Precio claro. Sin letras chiquitas.")}</h1>
        <p className="mt-6 max-w-xl text-[#5C5C5C] font-mono-data text-sm">
          {tr("Elige tu plan · elige cómo pagar · opcionalmente añade el módulo de Videos IA. Cancela cuando quieras. Soporte en español.")}
        </p>

        {/* STEP 1: Billing period */}
        <div className="mt-12 bg-white border border-[#0A0A0A] p-6" data-testid="pricing-step-1">
          <div className="flex items-baseline gap-3 mb-4">
            <span className="w-6 h-6 bg-[#0A0A0A] text-white font-mono-mark text-[10px] flex items-center justify-center">1</span>
            <div className="font-display text-2xl">{tr("¿Cómo quieres pagar?")}</div>
          </div>
          <div className="flex flex-wrap gap-3" data-testid="pricing-period-toggle">
            <button
              onClick={() => setPeriod("monthly")}
              className={`px-6 py-4 border-2 text-left ${period === "monthly" ? "border-[#E53935] bg-[#E53935] text-white" : "border-[#0A0A0A] bg-white hover:bg-[#F4F4F0]"}`}
              data-testid="pricing-toggle-monthly"
            >
              <div className="font-mono-mark text-[10px]">{tr("MENSUAL")}</div>
              <div className="font-display text-xl mt-1">{tr("Paga cada mes")}</div>
              <div className="font-mono-mark text-[10px] mt-1 opacity-80">{tr("Flexibilidad total")}</div>
            </button>
            <button
              onClick={() => setPeriod("annual")}
              className={`px-6 py-4 border-2 text-left relative ${period === "annual" ? "border-[#E53935] bg-[#E53935] text-white" : "border-[#0A0A0A] bg-white hover:bg-[#F4F4F0]"}`}
              data-testid="pricing-toggle-annual"
            >
              <span className={`absolute -top-3 right-3 font-mono-mark text-[9px] px-2 py-1 ${period === "annual" ? "bg-[#FFD600] text-[#0A0A0A]" : "bg-[#FFD600] text-[#0A0A0A]"}`}>
                {tr("2 MESES GRATIS")}
              </span>
              <div className="font-mono-mark text-[10px]">{tr("ANUAL")}</div>
              <div className="font-display text-xl mt-1">{tr("Paga 1 vez al año")}</div>
              <div className="font-mono-mark text-[10px] mt-1 opacity-80">{tr("Ahorra hasta 17%")}</div>
            </button>
          </div>
        </div>

        {/* STEP 2: Video addon */}
        <div className="mt-6 bg-white border border-[#0A0A0A] p-6" data-testid="pricing-step-2">
          <div className="flex items-baseline gap-3 mb-4">
            <span className="w-6 h-6 bg-[#0A0A0A] text-white font-mono-mark text-[10px] flex items-center justify-center">2</span>
            <div className="font-display text-2xl">{tr("¿Quieres videos IA?")}</div>
          </div>
          <div className="flex flex-wrap gap-3" data-testid="pricing-video-toggle">
            <button
              onClick={() => setAddVideo(false)}
              className={`px-6 py-4 border-2 text-left flex-1 min-w-[240px] ${!addVideo ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#0A0A0A] bg-white hover:bg-[#F4F4F0]"}`}
              data-testid="pricing-toggle-no-video"
            >
              <div className="font-mono-mark text-[10px]">{tr("SIN VIDEO")}</div>
              <div className="font-display text-xl mt-1">{tr("Solo texto + imágenes")}</div>
              <div className="font-mono-mark text-[10px] mt-1 opacity-80">{tr("Precio base del plan")}</div>
            </button>
            <button
              onClick={() => setAddVideo(true)}
              className={`px-6 py-4 border-2 text-left flex-1 min-w-[240px] relative ${addVideo ? "border-[#E53935] bg-[#E53935] text-white" : "border-[#0A0A0A] bg-white hover:bg-[#F4F4F0]"}`}
              data-testid="pricing-toggle-video"
            >
              <span className={`absolute -top-3 right-3 font-mono-mark text-[9px] px-2 py-1 bg-[#FFD600] text-[#0A0A0A]`}>
                {tr("PRECIO 2×")}
              </span>
              <div className="font-mono-mark text-[10px] flex items-center gap-2"><Video className="w-3 h-3" /> {tr("AÑADIR VIDEOS IA")}</div>
              <div className="font-display text-xl mt-1">{tr("Hasta 1 video/día")}</div>
              <div className="font-mono-mark text-[10px] mt-1 opacity-80">{tr("Tú eliges frecuencia")}</div>
            </button>
          </div>
          {addVideo && (
            <div className="mt-4 bg-[#FFD600] border border-[#0A0A0A] p-3 font-mono-data text-xs" data-testid="pricing-video-info">
              <b>{tr("Incluye:")}</b> {tr("generador de videos verticales (Reels, TikTok, Shorts) · 4, 8 ó 12 segundos · hasta 30 al mes · frecuencia configurable (diario · semanal · 15 días · personalizado).")}
            </div>
          )}
        </div>

        {/* STEP 3: Plan */}
        <div className="mt-6 bg-white border border-[#0A0A0A] p-6 md:p-8" data-testid="pricing-step-3">
          <div className="flex items-baseline gap-3 mb-6">
            <span className="w-6 h-6 bg-[#0A0A0A] text-white font-mono-mark text-[10px] flex items-center justify-center">3</span>
            <div className="font-display text-2xl">{tr("Elige tu plan")}</div>
          </div>

          {!user && (
            <p className="mb-4 font-mono-mark text-[10px] text-[#E53935]">
              {tr("⚠ PARA CONTRATAR NECESITAS UNA CUENTA ·")} <Link to="/register" className="underline">{tr("CREAR GRATIS")}</Link> · <Link to="/login" className="underline">{tr("ENTRAR")}</Link>
            </p>
          )}

          <div className="grid md:grid-cols-4 gap-0 border border-[#0A0A0A]">
            {plans.map((p, i) => {
              const h = p.highlight;
              const isCurrent = user?.plan === p.id;
              const { total, base, videoAdd, per, savings } = priceFor(p);
              const cardStyle = h ? "bg-[#0A0A0A] text-white" : "bg-white";
              const kickerStyle = h ? "text-[#FFD600]" : "text-[#5C5C5C]";
              return (
                <div
                  key={p.id}
                  className={`p-6 flex flex-col ${i < plans.length - 1 ? "border-b md:border-b-0 md:border-r border-[#0A0A0A]" : ""} ${cardStyle}`}
                  data-testid={`pricing-full-${p.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`font-mono-mark text-[10px] ${kickerStyle}`}>{tr(p.name).toUpperCase()}</span>
                    {h && <span className="bg-[#E53935] text-white font-mono-mark text-[9px] px-2 py-1">POPULAR</span>}
                  </div>

                  {/* BIG price */}
                  <div className="mt-6 flex items-baseline gap-1">
                    <span className="font-display text-6xl leading-none" data-testid={`pricing-total-${p.id}`}>{format(total)}</span>
                    <span className="font-mono-mark text-[10px]">/{tr(per)}</span>
                  </div>

                  {/* Breakdown when video active */}
                  {addVideo && p.id !== "free" && (
                    <div className={`mt-2 font-mono-data text-xs ${h ? "text-white/70" : "text-[#5C5C5C]"}`}>
                      {format(base)} plan + {format(videoAdd)} videos
                    </div>
                  )}

                  {/* Savings badge */}
                  {savings > 0 && (
                    <div className={`mt-2 font-mono-mark text-[10px] ${h ? "text-[#FFD600]" : "text-[#E53935]"}`}>
                      {tr("AHORRAS")} {format(savings)} / {tr("AÑO")}
                    </div>
                  )}

                  {/* Video addon indicator */}
                  {addVideo && p.id !== "free" && (
                    <div className={`mt-3 font-mono-mark text-[10px] px-2 py-1 inline-flex items-center gap-1 self-start ${h ? "bg-[#E53935] text-white" : "bg-[#FFD600] text-[#0A0A0A]"}`}>
                      <Video className="w-3 h-3" /> +VIDEOS IA
                    </div>
                  )}

                  <ul className="mt-6 space-y-2 text-sm font-mono-data flex-1">
                    {p.features.slice(0, 6).map((f) => (
                      <li key={f} className="flex gap-2">
                        <Check className="w-3 h-3 text-[#E53935] shrink-0 mt-1" />
                        <span>{tr(f)}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-6 pt-4 border-t border-[#0A0A0A]">
                    {isCurrent ? (
                      <div className="text-center font-mono-mark text-[10px] px-4 py-3 border border-[#E53935] text-[#E53935]" data-testid={`pricing-current-${p.id}`}>
                        PLAN ACTUAL
                      </div>
                    ) : user ? (
                      <CheckoutButton
                        planId={planKey(p.id)}
                        variant={h ? "red" : "outline"}
                        className="w-full"
                        data-testid={`pricing-full-cta-${p.id}`}
                      >
                        {tr("ELEGIR")} · {format(total)}/{tr(per)}
                      </CheckoutButton>
                    ) : (
                      <Link
                        to="/register"
                        className={`block text-center font-mono-mark text-[11px] px-4 py-3 border ${h ? "bg-[#E53935] text-white border-[#E53935]" : "bg-transparent text-[#0A0A0A] border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"}`}
                        data-testid={`pricing-full-cta-${p.id}`}
                      >
                        {tr("CREAR CUENTA PARA ELEGIR")}
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <p className="mt-8 font-mono-mark text-[10px] text-[#5C5C5C]">
            {info.code !== "USD" && (<span className="text-[#E53935]">{tr("PRECIOS APROXIMADOS · CARGO REAL EN USD POR STRIPE ·")} </span>)}
            PAGOS PROCESADOS POR STRIPE · MODO TEST · TARJETA 4242 4242 4242 4242 · CUALQUIER CVV · FECHA FUTURA
          </p>
        </div>
      </section>
    </div>
  );
}
